import React, { ReactElement, useCallback, useMemo } from "react";
import GlobalStoreContext from "@core-ui/components/GlobalStore/GlobalStoreContext";
import { Storage } from "@core-ui/helpers/Storage";
import { isEmpty } from "@core-ui/helpers/Utils";

type Props = {
    storeName?: string;
    children: ReactElement;
};

function GlobalStore({ children, storeName }: Props): ReactElement {
    const storeKeyName = storeName || "web-cart-global-store";

    const getKeyValue = useCallback(
        (key: string): string | undefined => {
            const store = Storage.getItem<Record<string, string>>(storeKeyName, {});
            return store?.[key];
        },
        [storeKeyName],
    );

    const setKeyValue = useCallback(
        (key: string, value: string) => {
            const store = Storage.getItem<Record<string, string>>(storeKeyName, {});
            const newStore = {
                ...store,
                [key]: value,
            };
            Storage.setItem<Record<string, string>>(storeKeyName, newStore);
        },
        [storeKeyName],
    );

    const deleteKey = useCallback(
        (key: string) => {
            const store = Storage.getItem<Record<string, string>>(storeKeyName, {});

            if (!isEmpty(store)) {
                delete store?.[key];
                Storage.setItem<Record<string, string>>(storeKeyName, store);
            }
        },
        [storeKeyName],
    );

    const deleteAll = useCallback(() => {
        Storage.setItem<Record<string, string>>(storeKeyName, {});
    }, [storeKeyName]);

    const contextValue = useMemo(() => {
        return {
            storeKeyName,
            set: setKeyValue,
            get: getKeyValue,
            delete: deleteKey,
            deleteAll: deleteAll,
        };
    }, [storeKeyName, setKeyValue, getKeyValue, deleteKey, deleteAll]);

    return <GlobalStoreContext.Provider value={contextValue}>{children}</GlobalStoreContext.Provider>;
}

export default GlobalStore;
